/**
 * 統合プロダクト（Gen1）案件詳細ページ「ダッシュボード」タブ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Gen1統合プロダクト1件のステータス・担当者・更新日・配下Gen2チャンクの加重平均健康度/pt集計・チャンク一覧を表示する
 * @graph-connects useIntegratedProductDetail [calls] 統合プロダクト単体取得
 * @graph-connects calcWeightedHealth [calls] 配下チャンクの加重平均健康度算出
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useIntegratedProductDetail } from '../../../hooks/useProjects.js';
import { HealthBadge, calcWeightedHealth } from '../health/index.js';
import { getStatusVariant } from './ChildTicketsModal.js';
import { ProgressBar } from './ProjectTable.js';
import type { Project } from '../../../types/project.js';

/** ダッシュボードの統計カード1枚。 @graph-connects none */
function StatCard({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3">
      <div className="text-xs text-[var(--color-text-muted)]">{label}</div>
      <div className="mt-1 text-sm font-medium text-[var(--color-text-primary)]">{value}</div>
    </div>
  );
}

/** 配下チャンク一覧テーブルの1行。issueKey は自分自身の案件詳細ページへリンクする。 @graph-connects none */
function ChunkProjectRow({ project }: { project: Project }) {
  return (
    <tr className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors">
      <td className="py-3 px-4 text-sm text-[var(--color-accent-primary)] whitespace-nowrap">
        <Link
          to="/projects/$pjKey"
          params={{ pjKey: project.issueKey }}
          className="hover:underline"
        >
          {project.issueKey}
        </Link>
      </td>
      <td
        className="py-3 px-4 text-sm text-[var(--color-text-primary)] truncate"
        title={project.summary}
      >
        {project.summary}
      </td>
      <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] truncate">
        {project.assignee ?? '-'}
      </td>
      <td className="py-3 px-4 text-sm">
        <ProgressBar ratio={project.completionRatio} />
      </td>
      <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)] whitespace-nowrap">
        {project.totalActualPoint}/{project.totalPlannedPoint}
      </td>
      <td className="py-3 px-4 text-sm text-center">
        <HealthBadge health={project.health} />
      </td>
      <td className="py-3 px-4 text-sm">
        <Badge variant={getStatusVariant(project.status)} size="sm">
          {project.status}
        </Badge>
      </td>
    </tr>
  );
}

/** @graph-connects useIntegratedProductDetail [calls] 統合プロダクト単体取得 */
export function IntegratedProductDashboardTab({ pjKey }: { pjKey: string }) {
  const { t } = useTranslation();
  const {
    data: integratedProduct,
    isLoading,
    error,
  } = useIntegratedProductDetail(pjKey, {
    enabled: true,
  });

  if (error) {
    return (
      <p className="pt-6 text-sm text-[var(--color-text-secondary)]">
        {t('integratedProductDetail.loadFailed')}
      </p>
    );
  }
  if (isLoading || !integratedProduct) {
    return <p className="pt-6 text-sm text-[var(--color-text-muted)]">{t('spec.loading')}</p>;
  }

  const { chunkProjects } = integratedProduct;
  const totalActual = chunkProjects.reduce((sum, p) => sum + p.totalActualPoint, 0);
  const totalPlanned = chunkProjects.reduce((sum, p) => sum + p.totalPlannedPoint, 0);
  const aggRatio = totalPlanned > 0 ? Math.round((totalActual / totalPlanned) * 1000) / 10 : null;
  const aggHealth = calcWeightedHealth(chunkProjects);

  return (
    <div className="space-y-6 pt-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label={t('projects.fields.status')}
          value={
            <Badge variant={getStatusVariant(integratedProduct.status)} size="sm">
              {integratedProduct.status}
            </Badge>
          }
        />
        <StatCard label={t('projects.health.title')} value={<HealthBadge health={aggHealth} />} />
        <StatCard label={t('projects.fields.assignee')} value={integratedProduct.assignee ?? '-'} />
        <StatCard
          label={t('integratedProductDetail.updatedAt')}
          value={(integratedProduct.updatedAt ?? '').slice(0, 10) || '-'}
        />
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3">
        <div className="mb-2 flex items-center justify-between text-xs text-[var(--color-text-muted)]">
          <span>{t('projects.fields.completionRatio')}</span>
          <span>
            {totalActual}/{totalPlanned}
          </span>
        </div>
        <ProgressBar ratio={aggRatio} />
      </div>

      <div>
        <h2 className="mb-3 text-base font-semibold">{t('projects.chunkProjects')}</h2>
        {chunkProjects.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('integratedProductDetail.noChunkProjects')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('projects.fields.issueKey')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('projects.fields.summary')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('projects.fields.assignee')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('projects.fields.completionRatio')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('projects.fields.points')}
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('projects.health.title')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('projects.fields.status')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {chunkProjects.map((p) => (
                  <ChunkProjectRow key={p.id} project={p} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
